/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { type GridConfig, ModuleLayout, getModuleLayoutConfig } from '@coursera-web/cds-labs';

import { useParams } from 'js/lib/useRouter';

import {
  Button,
  Grid,
  type GridProps,
  Hidden,
  Link,
  PageGridContainer,
  Typography2,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import type { PrivateCourseFieldsFragment } from 'bundles/enterprise-collections/queries/__generated__/PrivateCourses';
import EnterpriseCustomCourseProductCard from 'bundles/page-config-common/components/enterprise/EnterpriseCustomCourseProductCard';
import EnterpriseLearningPathProductCard from 'bundles/page-config-common/components/enterprise/EnterpriseLearningPathProductCard';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import type { EnterpriseBadgeWithCollection } from 'bundles/page-config-common/providers/enterprise/EnterpriseBadgeCollectionsProvider';

import _t from 'i18n!nls/consumer-home';

type Props = {
  enterpriseBadgesWithCollection: EnterpriseBadgeWithCollection[];
  privateCourses: PrivateCourseFieldsFragment[];
  orgName?: string;
};

const styles = {
  title: css`
    ${breakpoints.up('sm')} {
      font-size: 24px;
    }
  `,
  description: css`
    ${breakpoints.down('sm')} {
      padding: 4px 0;
    }

    padding: 16px 0;
  `,
  textContainer: css`
    align-content: center;
    height: 100%;
  `,
  container: css`
    ${breakpoints.up('sm')} {
      border-radius: var(--cds-border-radius-200);
      overflow: hidden;
    }

    padding: 0;
  `,
  mobileBackground: css`
    ${breakpoints.down('xs')} {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }
  `,
  mobileButton: css`
    padding-top: var(--cds-spacing-100);
  `,
  itemList: css`
    list-style: none;
    padding: 0;
  `,
};

const EnterpriseTailoredLearningModule = ({ enterpriseBadgesWithCollection, privateCourses, orgName }: Props) => {
  const { currentLevel } = useHeaderLevel();
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const isDesktopOrSmaller = useMediaQuery(breakpoints.down('md'));
  const track = useTracker();
  const { programSlug } = useParams();

  const onViewAllClick = () => {
    track('click_button', {
      button: {
        name: 'view_all_products',
        linkName: _t('View all'),
        linkType: 'internal',
        linkURL: `programs/${programSlug}/view-all-collections/tailored-learning`,
      },
    });
  };

  let layout = 'text3/image9';
  if (isTabletOrSmaller) {
    layout = 'text-top/image-bottom';
  } else if (isDesktopOrSmaller || enterpriseBadgesWithCollection.length < 3) {
    layout = 'text4/image8';
  }

  const hasLearningPaths = !!enterpriseBadgesWithCollection?.length;
  const hasCustomCourses = !!privateCourses?.length;

  if (!hasLearningPaths && !hasCustomCourses) {
    return null;
  }

  // display up to 3 cards on lg and xl breakpoints or up to 2 cards for md, sm, xs
  const numCardsToDisplay = isDesktopOrSmaller ? 2 : 3;
  let productsToDisplay: Array<EnterpriseBadgeWithCollection | PrivateCourseFieldsFragment> = [];

  if (hasLearningPaths && hasCustomCourses) {
    const learningPaths = enterpriseBadgesWithCollection.slice(0, 2);
    const customCourses = privateCourses.slice(0, 2);
    // put custom course in the middle, if no 2nd learning path add another custom course
    const orderedProducts = [learningPaths[0], customCourses[0], learningPaths[1], customCourses[1]].filter(Boolean);
    productsToDisplay = orderedProducts.slice(0, numCardsToDisplay);
  } else {
    const products = hasLearningPaths ? enterpriseBadgesWithCollection : privateCourses;
    productsToDisplay = products.slice(0, numCardsToDisplay);
  }

  const isOneProduct = productsToDisplay.length === 1;
  const isTwoProducts = productsToDisplay.length === 2;

  const gridSpacing: GridProps['spacing'] = isOneProduct ? 0 : { xs: 8, md: 24 };
  const gridJustification = isOneProduct ? 'flex-end' : 'flex-start';
  let gridConfig: GridConfig = { xs: 12, sm: 6, md: 4 };
  if (isOneProduct) {
    gridConfig = { xs: 12, sm: 12, md: 9 };
  } else if (isDesktopOrSmaller || isTwoProducts) {
    gridConfig = { xs: 12, sm: 6, md: 6 };
  }

  const renderImage = () => (
    <Grid container>
      <Grid
        container
        item
        xs={12}
        spacing={gridSpacing}
        justifyContent={gridJustification}
        component="ul"
        css={styles.itemList}
      >
        {productsToDisplay.map((item, itemIndex) => {
          if (item.__typename === 'EnterpriseBadge_badgeCollectionWithUserState') {
            return (
              <Grid key={item.id} item {...gridConfig} component="li">
                <EnterpriseLearningPathProductCard
                  isDesktopListView={isOneProduct}
                  enterpriseBadgeWithCollection={item}
                  itemIndex={itemIndex}
                />
              </Grid>
            );
          } else if (item.__typename === 'Course_Course') {
            return (
              <Grid key={item.id} item {...gridConfig} component="li">
                <EnterpriseCustomCourseProductCard
                  isDesktopListView={isOneProduct}
                  customCourse={item}
                  itemIndex={itemIndex}
                />
              </Grid>
            );
          } else {
            return null;
          }
        })}
      </Grid>
      <Grid item xs={12} component={Hidden} mdUp css={styles.mobileButton}>
        <Button
          icon={<ArrowNextIcon />}
          iconPosition="after"
          size="medium"
          variant="ghost"
          fullWidth
          onClick={onViewAllClick}
          component={Link}
          href={`programs/${programSlug}/view-all-collections/tailored-learning`}
        >
          {_t('View all')}
        </Button>
      </Grid>
    </Grid>
  );

  const renderContent = () => (
    <div css={styles.textContainer}>
      <Typography2 variant="subtitleLarge" component={currentLevel} css={styles.title}>
        {_t('Content tailored for learning at #{orgName}', { orgName: orgName || _t('your organization') })}
      </Typography2>
      <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.description}>
        {_t('Custom learning paths and courses chosen by your organization')}
      </Typography2>
      <Hidden smDown>
        <Button
          edgeAlign="start"
          icon={<ArrowNextIcon />}
          iconPosition="after"
          size="medium"
          variant="ghost"
          onClick={onViewAllClick}
          component={Link}
          href={`programs/${programSlug}/view-all-collections/tailored-learning`}
        >
          {_t('View all')}
        </Button>
      </Hidden>
    </div>
  );
  const configProps = {
    layout,
    backgroundColor: 'grey',
  };

  const layoutConfig = getModuleLayoutConfig(configProps);

  return (
    <PageGridContainer data-testid="enterprise-learning-paths" css={styles.mobileBackground}>
      <Grid item xs={12}>
        <div css={styles.container}>
          <ModuleLayout renderContent={renderContent} renderImage={renderImage} assembly={layoutConfig} />
        </div>
      </Grid>
    </PageGridContainer>
  );
};

export default EnterpriseTailoredLearningModule;
